import { forwardRef, useState, type ComponentPropsWithoutRef } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useLanguage } from "@/i18n/LanguageContext";
import { useAuthState } from "@/hooks/useAuthState";
import { useHasActivePanel } from "@/hooks/useHasActivePanel";
import { startProCheckout } from "@/lib/proCheckout";
import PlanComparisonPanel, { PREMIUM_MODULE_COUNT } from "@/components/plan/PlanComparisonPanel";
import { DEFAULT_PLAN_COMPARISON, planComparisonQueryOptions } from "@/lib/planComparisonContent";
import { useAdminPlanView } from "@/hooks/useAdminPlanView";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export { PREMIUM_MODULE_COUNT };

/**
 * Debe reenviar props y ref: es el disparador del popover/sheet de Radix.
 */
const ChipButton = forwardRef<
  HTMLButtonElement,
  { label: string; showAlert: boolean } & ComponentPropsWithoutRef<"button">
>(({ label, showAlert, ...props }, ref) => (
  <Button
    ref={ref}
    type="button"
    variant="outline"
    size="sm"
    className="relative h-9 rounded-full border-border bg-card px-3 text-xs font-bold shadow-none"
    aria-label={label}
    {...props}
  >
    {label}
    {showAlert && (
      <span
        className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border-2 border-card bg-destructive"
        aria-hidden="true"
      />
    )}
  </Button>
));
ChipButton.displayName = "ChipButton";

const ComparisonContent = ({
  content,
  checkout,
  close,
  sideOffset,
  open,
  retailSector,
}: {
  content: Parameters<typeof PlanComparisonPanel>[0]["content"];
  checkout: () => Promise<void>;
  close: () => void;
  sideOffset: number;
  open: boolean;
  retailSector?: string | null | undefined;
}) => (
  <>
    {open && typeof document !== "undefined"
      ? createPortal(
          <div
            aria-hidden="true"
            className="fixed inset-0 z-40 bg-card/10 backdrop-blur-[2px] transition-opacity duration-200"
            onClick={close}
            onPointerDown={(event) => event.stopPropagation()}
          />,
          document.body,
        )
      : null}
    <PopoverContent
      align="end"
      side="bottom"
      sideOffset={sideOffset}
      collisionPadding={12}
      onOpenAutoFocus={(event) => event.preventDefault()}
      className="w-auto border-0 bg-transparent p-0 shadow-none before:absolute before:right-5 before:-top-1.5 before:z-20 before:h-3 before:w-3 before:rotate-45 before:border-l before:border-t before:border-plan-popup-border before:bg-card/88 motion-reduce:animate-none"
    >
      <PlanComparisonPanel content={content} onCheckout={checkout} retailSector={retailSector} />
    </PopoverContent>
  </>
);

export default function PlanStatusChip({ hasPro, showAlert }: { hasPro?: boolean; showAlert?: boolean }) {
  const { lang } = useLanguage();
  const { isAuthenticated, isReady, user } = useAuthState();
  // Rol real: el selector Lite/Pro del administrador debe seguir visible
  // aunque esté simulando la vista Lite.
  const plan = useHasActivePanel({ ignoreAdminView: true });
  const { data: comparison } = useQuery(planComparisonQueryOptions);
  const { view: adminView, setView: setAdminView } = useAdminPlanView(plan.isAdmin);
  const [comparisonOpen, setComparisonOpen] = useState(false);
  const { data: retailSectorData } = useQuery({
    queryKey: ["plan-comparison-sector", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data } = await supabase
        .from("profiles")
        .select("retail_sector")
        .eq("user_id", user.id)
        .maybeSingle();
      return data?.retail_sector ?? null;
    },
    enabled: Boolean(user?.id),
    staleTime: 5 * 60 * 1000,
  });
  const retailSector = retailSectorData ?? null;

  if (!isReady || !isAuthenticated) return null;

  const realPro = hasPro ?? (plan.hasPanel || plan.isAdmin);

  // Club Pro: chip informativo, sin ventana comparativa (ya no hay nada que mejorar).
  if (realPro && !plan.isAdmin) {
    return (
      <div className="pointer-events-none inline-flex items-center">
        <ChipButton label="Pro" showAlert={false} aria-disabled="true" />
      </div>
    );
  }

  const content = (comparison ?? DEFAULT_PLAN_COMPARISON)[lang === "es" ? "es" : "en"];
  const alert = showAlert ?? (plan.isAdmin ? false : true);
  const checkout = async () => {
    const result = await startProCheckout({ isAuthenticated: true, lang: lang === "es" ? "es" : "en" });
    if (result === "error") {
      toast.error(
        lang === "es"
          ? "No hemos podido abrir el pago. Inténtalo de nuevo en un momento."
          : "We couldn't open the payment page. Please try again in a moment.",
      );
    }
  };

  // Administrador: el selector de planes vive en la barra de administrador
  // (AdminPlanSwitcher); aquí no se pinta nada para no duplicar controles.
  if (plan.isAdmin) return null;
  if (false as boolean) {
    return (
      <div className="inline-flex items-center rounded-full border border-border bg-card p-0.5">
        <Popover open={comparisonOpen} onOpenChange={setComparisonOpen} modal={false}>
          <PopoverTrigger asChild>
            <button
              type="button"
              onClick={() => setAdminView("lite")}
              aria-pressed={adminView === "lite"}
              className={`h-8 rounded-full px-3 text-xs font-bold transition-colors ${
                adminView === "lite"
                  ? "bg-foreground text-background"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {lang === "es" ? "Sin plan" : "No plan"}
            </button>
          </PopoverTrigger>
          <ComparisonContent
            content={content}
            checkout={checkout}
            close={() => setComparisonOpen(false)}
            sideOffset={14}
            open={comparisonOpen}
            retailSector={retailSector}
          />
        </Popover>
        <button
          type="button"
          onClick={() => setAdminView("pro")}
          aria-pressed={adminView === "pro"}
          className={`h-8 rounded-full px-3 text-xs font-bold transition-colors ${
            adminView === "pro"
              ? "bg-foreground text-background"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          Pro
        </button>
      </div>
    );
  }

  return (
    <div className="inline-flex items-center">
      <Popover open={comparisonOpen} onOpenChange={setComparisonOpen} modal={false}>
        <PopoverTrigger asChild>
          <ChipButton label={lang === "es" ? "Sin plan" : "No plan"} showAlert={alert} />
        </PopoverTrigger>
        <ComparisonContent
          content={content}
          checkout={checkout}
          close={() => setComparisonOpen(false)}
          sideOffset={10}
          open={comparisonOpen}
          retailSector={retailSector}
        />
      </Popover>
    </div>
  );
}
